import React from 'react';
import { Button, Flex } from '@patternfly/react-core';
import { CopyIcon } from '@patternfly/react-icons';
import { useTranslation } from 'react-i18next';

import './labeling-info.css';

import { CEMarkIcon, DateOfManufactureIcon, ManufacturerIcon, MDMarkIcon, PrescriptionOnlyIcon, UDIMarkIcon, WorkspaceLogo, ConsultIFUIcon } from '../../img';
import ExternalLink from '../../components/external-link';
import { MVisionInfo } from '../../store/appConfig/appDeploymentInfoTypes';
import { WORKSPACE_NAME } from '../../environments';


interface LabelingInfoProps {
    softwareVersion: string;
    clinicalUse: boolean;
    description?: string;
    udi?: string;
    manufacturer?: string,
    manufactureDate?: string;
    mvisionInfo?: MVisionInfo;
    showManufacturingInformation?: boolean;
    showFooter?: boolean;
    moduleName?: string;
    className?: string;
    getLabelInfoForClipboard: () => string;
}

export const getLabelForIsForClinicalUse = (isForClinicalUse: boolean) => {
    return isForClinicalUse ? "Yes" : "No";
};

const LabelingInfo = (props: LabelingInfoProps) => {
    const {
        softwareVersion,
        clinicalUse,
        description,
        udi,
        manufacturer = 'MVision AI',
        manufactureDate = 'N/A',
        mvisionInfo,
        showManufacturingInformation = true,
        showFooter = true,
        moduleName = WORKSPACE_NAME,  // use Workspace+ if not defined
        className = '',
        getLabelInfoForClipboard,
    } = props;

    const { t } = useTranslation();


    const handleLabelToClipboard = () => {

        const info = getLabelInfoForClipboard();

        navigator.clipboard.writeText(info).then(() => {
            alert('Product information copied to clipboard!');
        }).catch(err => {
            console.error('Failed to copy: ', err);
        });
    };

    return (
        <div className={`workspace-info ${className}`}>

            <h1>{moduleName}</h1>

            <div className="info-grid">
                <div className="info-section">
                    <h2 className='grey-title'>Software Version</h2>
                    <p data-testid="about-software-version">{softwareVersion}</p>
                </div>

                <div className="info-section">
                    <h2 className='grey-title'>Clinical use</h2>
                    <p data-testid="about-clinical-use">{getLabelForIsForClinicalUse(clinicalUse)}</p>
                </div>

                {mvisionInfo?.ifuUrl && (
                    <div className="info-section">
                        <div className="ifu-icon-container">
                            <img src={ConsultIFUIcon} alt="Date of Manufacture" className="ifu-icon" />
                            <div>
                                <h2 className='grey-title'>Instructions for use</h2>
                                <span data-testid="about-ifu-link">
                                    <ExternalLink url={mvisionInfo.ifuUrl} className="link-text">
                                        {mvisionInfo.ifuUrl}
                                    </ExternalLink>
                                </span>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div className="documents-section">
                <h2 className='grey-title'>Documents</h2>
                <p>{t('page.about.pdfInstructions')}</p>
            </div>

            {description && (
                <div className="description-section">
                    <h2 className='grey-title'>Description</h2>
                    <p>{description}</p>
                </div>
            )}

            {showManufacturingInformation && (
                <div className="address-section">
                    <div className="udi-section">
                        <img src={ManufacturerIcon} alt="Manufacturer" className="cert-icon" data-testid="about-manufacturer-mark" />
                        <div className="address-text">

                            {mvisionInfo?.address !== undefined ? (
                                <>
                                    <p>{mvisionInfo.address.company}</p>
                                    <p>{mvisionInfo.address.street}</p>
                                    <p>{mvisionInfo.address.location}</p>
                                </>
                            ) : (
                                <p>{manufacturer}</p>
                            )}
                        </div>
                    </div>
                    <div className="udi-section">
                        <img src={DateOfManufactureIcon} alt="Date of Manufacture" className="cert-icon" />
                        <div className="address-text">
                            <p>{manufactureDate}</p>
                        </div>
                    </div>
                    {udi && (
                        <div className="udi-section">
                            <div className="udi-box">
                                <img src={UDIMarkIcon} alt="UDI Mark" className="cert-icon" data-testid="about-udi-mark" />
                                <span data-testid="about-udi-value">{udi}</span>
                            </div>
                        </div>
                    )}
                </div>
            )}

            <div className="more-info-section">
                {(mvisionInfo?.email || mvisionInfo?.webAddress) && (
                    <div>
                        <h2 className='grey-title'>More information</h2>
                        {mvisionInfo?.email && (<Flex gap={{ default: 'gapXs' }}>Email:<a href={`mailto:${mvisionInfo.email}`} className="info-link">{mvisionInfo.email}</a></Flex>)}
                        {mvisionInfo?.webAddress && (<Flex gap={{ default: 'gapXs' }}>Web:<ExternalLink url={mvisionInfo.webAddress} className="info-link">{mvisionInfo.webAddress}</ExternalLink></Flex>)}
                    </div>
                )}
                <Button onClick={handleLabelToClipboard} variant="secondary" icon={<CopyIcon />}>Copy product information</Button>
            </div>

            {showFooter && (
                <div className="footer">
                    <img src={WorkspaceLogo} alt="Workspace Logo" className="cert-icon" />
                    <div className="certifications">
                        <img src={CEMarkIcon} alt="CE Mark" className="cert-icon" />
                        {/* <img src={ECRepIcon} alt="EC Representative" className="cert-icon" /> */}
                        <img src={PrescriptionOnlyIcon} alt="Prescription Only" className="cert-icon" />
                        <img src={MDMarkIcon} alt="MD Mark" className="cert-icon" />
                    </div>
                </div>
            )}

        </div>
    );
};

export default LabelingInfo;
